import React, { ReactElement } from "react";
import { array, boolean, date, number, object, string } from "yup";
import {
  INVALID_DATE_VALIDATION_MESSAGE,
  MANDATORY_ENTRY_VALIDATION_MESSAGE,
  MANDATORY_FILED_VALIDATION_MESSAGE,
  MAX_LENGTH_50_VALIDATION_MESSAGE,
} from "shared/constants";
import {
  CellTypeEnum,
  ColumnType,
  DateCell,
  InputCell,
  NumericCell,
  RenderCellProps,
} from "core";
import { MenuItem, Select as MuiSelect } from "@mui/material";
import { nanToNull } from "shared/utils";

const formSchema = {
  id: number().transform(nanToNull).nullable(),
  percentageReceived: number()
    .transform(nanToNull)
    .nullable()
    .required(MANDATORY_FILED_VALIDATION_MESSAGE),
  paidWithinNumberOfDays: number()
    .transform(nanToNull)
    .nullable()
    .required(MANDATORY_FILED_VALIDATION_MESSAGE),
  startDate: date()
    .nullable()
    .typeError(INVALID_DATE_VALIDATION_MESSAGE)
    .required(MANDATORY_FILED_VALIDATION_MESSAGE),
  deductedFromPayment: number()
    .transform(nanToNull)
    .nullable()
    .required(MANDATORY_FILED_VALIDATION_MESSAGE),
  isVATIncluded: number()
    .transform(nanToNull)
    .nullable()
    .required(MANDATORY_FILED_VALIDATION_MESSAGE),
};

export const validationSchema = object().shape({
  supplierID: string()
    .trim()
    .max(50, MAX_LENGTH_50_VALIDATION_MESSAGE)
    .nullable(),
  avb: boolean().nullable(),
  epd: boolean().nullable(),
  msi: boolean().nullable(),
  entries: array()
    .of(object().shape(formSchema))
    .when("epd", {
      is: true,
      then: array()
        .required(MANDATORY_ENTRY_VALIDATION_MESSAGE)
        .min(1, MANDATORY_ENTRY_VALIDATION_MESSAGE),
      otherwise: array().nullable(),
    }),
});

export const columns: ColumnType[] = [
  {
    field: "id",
    headerName: "Id",
    hide: true,
    cellType: CellTypeEnum.Input,
    renderCell: ({ field }: RenderCellProps) => <InputCell {...field} />,
  },
  {
    field: "percentageReceived",
    headerName: "Percentage Received",
    cellType: CellTypeEnum.NumberFormat,
    renderCell: ({ field }: RenderCellProps) => (
      <NumericCell suffix="%" {...field} />
    ),
  },
  {
    field: "paidWithinNumberOfDays",
    headerName: "Paid Within Number Of Days",
    cellType: CellTypeEnum.NumberFormat,
    renderCell: ({ field }: RenderCellProps) => (
      <NumericCell integer {...field} />
    ),
  },
  {
    field: "startDate",
    headerName: "Start Date",
    cellType: CellTypeEnum.Date,
    renderCell: ({ field }: RenderCellProps) => <DateCell {...field} />,
  },
  {
    field: "deductedFromPayment",
    headerName: "Deducted From Payment",
    cellType: CellTypeEnum.Select,
    renderCell: ({ field, options = [] }: RenderCellProps): ReactElement => {
      return (
        <MuiSelect
          disabled
          {...field}
          IconComponent={() => <></>}
          SelectDisplayProps={{ style: { paddingTop: 8, paddingBottom: 8 } }}
        >
          {options.map(({ key, value }: OptionType) => {
            return (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            );
          })}
        </MuiSelect>
      );
    },
  },
  {
    field: "isVATIncluded",
    headerName: "VAT",
    cellType: CellTypeEnum.Select,
    renderCell: ({ field, options = [] }: RenderCellProps): ReactElement => {
      return (
        <MuiSelect
          disabled
          {...field}
          IconComponent={() => <></>}
          SelectDisplayProps={{ style: { paddingTop: 8, paddingBottom: 8 } }}
        >
          {options.map(({ key, value }: OptionType) => {
            return (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            );
          })}
        </MuiSelect>
      );
    },
  },
];
